/**
 * Public résumé view — gated to verified employers.
 *
 * When an employer opens /r/<public_id> we fetch the résumé from
 * /api/discover/<id> (verified-only), bump the view counter, and render a
 * clean full-page view with the candidate's summary, skills, and contact
 * button.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, MapPin, Users, Sparkles, Mail, ShieldCheck, Eye,
  Send, X, Loader, CheckCircle2, Zap,
} from "lucide-react";
import SaveCandidateButton from "../components/employer/SaveCandidateButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicResume() {
  const { public_id } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [initiallySaved, setInitiallySaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/discover/${public_id}`, { withCredentials: true }),
          axios.post(`${BACKEND_URL}/api/employer/shortlist/status/batch`,
            { public_ids: [public_id] }, { withCredentials: true }).catch(() => ({ data: { saved: [] } })),
        ]);
        setResume(r.data);
        setInitiallySaved((s?.data?.saved || []).includes(public_id));
      } catch (err) {
        setError(err?.response?.data?.detail || "Résumé not available.");
      } finally {
        setLoading(false);
      }
    })();
  }, [public_id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8FAFC" }}>
      <div className="font-mono-rm text-[11px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
        · Loading résumé
      </div>
    </div>
  );

  if (error || !resume) return (
    <div className="min-h-screen py-8 px-4" style={{ background: "#F8FAFC" }}>
      <div className="max-w-2xl mx-auto text-center rounded-md p-6"
           style={{ background: "white", border: "1.5px solid #CBD5E1" }}
           data-testid="public-resume-error">
        <div className="font-display uppercase mb-2" style={{ color: "#0F1B2D" }}>Not available</div>
        <p style={{ color: "#475569" }}>{error || "Try again in a bit."}</p>
        <Link to="/discover" className="inline-block mt-4 font-mono-rm text-[10px] uppercase tracking-widest"
              style={{ color: "#FF6A00" }}>← Back to directory</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-6 px-4" style={{ background: "#F8FAFC" }}
         data-testid="public-resume-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/discover"
              className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest mb-3"
              style={{ color: "#94A3B8" }} data-testid="public-resume-back">
          <ArrowLeft size={12} /> Directory
        </Link>
        <div className="rounded-md p-6 md:p-8"
             style={{ background: "white", border: "2px solid #0F1B2D", boxShadow: "6px 6px 0 0 #FFC145" }}>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div className="min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                   style={{ color: "#FF6A00", fontWeight: 700 }}>
                · Verified by Resmo · Published résumé
              </div>
              <h1 className="font-display uppercase leading-tight"
                  style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                  data-testid="public-resume-name">
                {resume.name_display}
              </h1>
              {resume.headline && (
                <div className="text-[15px] font-medium mt-1" style={{ color: "#334155" }}
                     data-testid="public-resume-headline">
                  {resume.headline}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SaveCandidateButton
                publicId={public_id}
                initialSaved={initiallySaved}
                variant="large"
                testIdPrefix="public-resume-save"
              />
              <div className="flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
                   style={{ background: "#F1F5F9", color: "#64748B", border: "1px solid #E2E8F0" }}
                   data-testid="public-resume-views" title="Total employer views">
                <Eye size={11} /> {resume.view_count || 0} views
              </div>
            </div>
          </div>

          {resume.superpower && (
            <div
              className="rounded p-3 flex items-start gap-2 mb-4"
              style={{ background: "#FFF2EC", border: "1.5px dashed #FF6A00" }}
              data-testid="public-resume-superpower"
            >
              {resume.superpower_avatar_url ? (
                <img src={resume.superpower_avatar_url} alt="superpower"
                     style={{ width: 40, height: 40, borderRadius: 6, border: "1.5px solid #0F1B2D", objectFit: "cover" }} />
              ) : (
                <span
                  className="inline-flex items-center justify-center rounded"
                  style={{ width: 40, height: 40, background: "#0F1B2D", color: "#FFC145", border: "1.5px solid #0F1B2D" }}
                >
                  <Zap size={20} />
                </span>
              )}
              <div className="min-w-0">
                <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#FF6A00", fontWeight: 700 }}>
                  · Superpower
                </div>
                <div className="text-[14px] leading-snug" style={{ color: "#0F1B2D", fontWeight: 600 }}>
                  {resume.superpower}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mb-5">
            {resume.location && (
              <span className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded"
                    style={{ background: "#F1F5F9", color: "#334155" }}>
                <MapPin size={12} /> {resume.location}
              </span>
            )}
            {resume.years_experience > 0 && (
              <span className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded"
                    style={{ background: "#FEF3C7", color: "#CA8A04", fontWeight: 600 }}>
                <Users size={12} /> {resume.years_experience} years of experience
              </span>
            )}
          </div>

          {resume.summary && (
            <div className="mb-5">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                   style={{ color: "#94A3B8", fontWeight: 700 }}>· Summary</div>
              <p className="text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "#334155" }}
                 data-testid="public-resume-summary">
                {resume.summary}
              </p>
            </div>
          )}

          {resume.skills?.length > 0 && (
            <div className="mb-6">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-2"
                   style={{ color: "#94A3B8", fontWeight: 700 }}>· Skills</div>
              <div className="flex flex-wrap gap-1.5" data-testid="public-resume-skills">
                {resume.skills.map((s, i) => (
                  <span key={i} className="text-[12px] px-2 py-1 rounded"
                        style={{ background: "#F1F5F9", color: "#0F1B2D", border: "1px solid #CBD5E1", fontWeight: 500 }}>
                    <Sparkles size={10} className="inline mr-1" style={{ color: "#FF6A00" }} />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="rounded p-4"
               style={{ background: "#FEF3C7", border: "1.5px dashed #FFC145" }}
               data-testid="public-resume-contact-panel">
            {resume.email ? (
              <div>
                <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                     style={{ color: "#CA8A04", fontWeight: 700 }}>· Contact directly</div>
                <a href={`mailto:${resume.email}`}
                   className="inline-flex items-center gap-2 rounded px-3 py-1.5 font-mono-rm uppercase text-[11px] tracking-widest"
                   style={{ background: "#0F1B2D", color: "#FFC145", border: "1.5px solid #0F1B2D", fontWeight: 700, textDecoration: "none" }}
                   data-testid="public-resume-email-btn">
                  <Mail size={12} /> {resume.email}
                </a>
              </div>
            ) : (contactSent || resume.existing_request) ? (
              <div className="flex items-start gap-2" data-testid="public-resume-contact-sent">
                <CheckCircle2 size={18} style={{ color: "#0E5A4A", flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                       style={{ color: "#0E5A4A", fontWeight: 700 }}>
                    · {resume.existing_request && !contactSent
                        ? (resume.existing_request.status === "accepted"
                             ? "Already accepted"
                             : "Request already open")
                        : "Request sent"}
                  </div>
                  <p className="text-[12px] mt-1" style={{ color: "#064E3B" }}>
                    {contactSent
                      ? <>{resume.name_display} has been notified. You&apos;ll get an email when they respond.</>
                      : resume.existing_request?.status === "accepted"
                        ? <>{resume.name_display} accepted your earlier request. Check your outbox for their reply.</>
                        : <>You already have a pending request with {resume.name_display}. Please wait for their response.</>
                    }
                    {" "}Track your requests at{" "}
                    <Link to="/employer/requests" style={{ color: "#0F1B2D", fontWeight: 700 }}>
                      Your outbox →
                    </Link>
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start gap-2 mb-3">
                  <ShieldCheck size={16} style={{ color: "#CA8A04", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                         style={{ color: "#CA8A04", fontWeight: 700 }}>
                      · Contact via Resmo relay
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: "#7C2D12" }}>
                      This candidate keeps their email private. Send a short message
                      through Resmo — they&apos;ll get notified and can accept or decline
                      before their contact info is shared.
                    </p>
                  </div>
                </div>
                <button onClick={() => setContactOpen(true)}
                        className="inline-flex items-center gap-2 rounded px-3 py-1.5 font-mono-rm uppercase text-[11px] tracking-widest"
                        style={{ background: "#FF6A00", color: "white",
                                 border: "1.5px solid #0F1B2D", fontWeight: 700,
                                 boxShadow: "2px 2px 0 0 #0F1B2D" }}
                        data-testid="public-resume-send-relay-btn">
                  <Send size={12} /> Send introduction
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {contactOpen && (
        <ContactRelayModal
          publicId={public_id}
          candidateName={resume.name_display}
          onClose={() => setContactOpen(false)}
          onSent={() => { setContactSent(true); setContactOpen(false); }}
        />
      )}
    </div>
  );
}

function ContactRelayModal({ publicId, candidateName, onClose, onSent }) {
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async (e) => {
    e.preventDefault();
    setError("");
    if (role.trim().length < 2 || message.trim().length < 20) {
      setError("Please add a role title and a message of at least 20 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/discover/${publicId}/contact`,
        { role_title: role.trim(), message: message.trim() },
        { withCredentials: true });
      onSent();
    } catch (err) {
      setError(err?.response?.data?.detail
               || "Couldn't send the message. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
         style={{ background: "rgba(15,27,45,0.55)" }}
         onClick={onClose}
         data-testid="public-resume-contact-modal">
      <form onClick={(e) => e.stopPropagation()}
            onSubmit={send}
            className="w-full max-w-lg rounded-md p-5 md:p-6"
            style={{ background: "white", border: "2px solid #0F1B2D",
                     boxShadow: "6px 6px 0 0 #FFC145" }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#FF6A00", fontWeight: 700 }}>
              · Send an introduction to
            </div>
            <div className="font-display uppercase leading-tight"
                 style={{ fontSize: "1.35rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              {candidateName}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="p-1 rounded" style={{ color: "#94A3B8" }}
                  data-testid="public-resume-modal-close">
            <X size={18} />
          </button>
        </div>
        <label className="block mb-2">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "#475569", fontWeight: 700 }}>· Role title</div>
          <input type="text" value={role} onChange={(e) => setRole(e.target.value)}
                 placeholder="Senior Product Analyst"
                 className="w-full rounded px-3 py-2 text-[13px]"
                 style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                 data-testid="public-resume-modal-role"
                 maxLength={120} />
        </label>
        <label className="block mb-2">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "#475569", fontWeight: 700 }}>· Your message</div>
          <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hi — we saw your Resmo profile and love your background in X. We're hiring for Y at Z. Would you have 20 minutes for an intro call next week?"
                    className="w-full rounded px-3 py-2 text-[13px]"
                    style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", resize: "vertical" }}
                    data-testid="public-resume-modal-message"
                    maxLength={1200} />
          <div className="text-right text-[10px] font-mono-rm mt-0.5" style={{ color: "#94A3B8" }}>
            {message.length}/1200
          </div>
        </label>
        {error && (
          <div className="text-[12px] rounded px-3 py-2 mb-2"
               style={{ background: "#FEE2E2", color: "#B91C1C", border: "1px solid #FCA5A5" }}
               data-testid="public-resume-modal-error">
            {error}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="text-[11px]" style={{ color: "#94A3B8" }}>
            <ShieldCheck size={11} className="inline mr-1" />
            Their email stays private until they accept.
          </div>
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-2 rounded px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                  style={{ background: busy ? "#94A3B8" : "#FF6A00", color: "white",
                           border: "2px solid #0F1B2D", boxShadow: "3px 3px 0 0 #0F1B2D",
                           fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
                  data-testid="public-resume-modal-submit">
            {busy ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
            {busy ? "Sending…" : "Send introduction"}
          </button>
        </div>
      </form>
    </div>
  );
}
